'use client';

import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { PageSection } from '@/components';
import { MONO_FONT } from '@/theme';
import { useStaggerIn } from '@/hooks/useStaggerIn';

const STEPS = [
  {
    num: 'PASO 01',
    title: 'Conecta tu marca',
    desc: 'Dominio, marcas relacionadas y competidores. Importamos schema y registros públicos. Sin código, sin acceso a tu stack.',
  },
  {
    num: 'PASO 02',
    title: 'Audita y diagnostica',
    desc: 'Diagnóstico 0–100 en 4 dimensiones, mapa de brechas por motor y selección automática de acciones según tu arquetipo.',
  },
  {
    num: 'PASO 03',
    title: 'Activa el playbook',
    desc: 'Lo ejecuta tu equipo o el nuestro. Cada acción con esfuerzo, impacto y una condición explícita para escalar.',
  },
  {
    num: 'PASO 04',
    title: 'Mide la curva',
    desc: 'Reportes ejecutivos semanales. Citas acumuladas, share-of-voice y delta competitivo en cada motor.',
  },
];

export default function Steps() {
  const gridRef = useStaggerIn({ stagger: 0.1 });

  return (
    <PageSection
      dark
      eyebrow="05 / Flujo Operativo"
      title={
        <>
          <T>Cuatro pasos,</T>{' '}
          <Box component="span" sx={{ color: 'primary.lightAccent' }}>
            <T>72 horas</T>
          </Box>
          <Box component="br" sx={{ display: { xs: 'none', sm: 'block' } }} />{' '}
          <T>al primer reporte.</T>
        </>
      }
      lede="Primer reporte en 72 horas, sin instalar nada. No pegas código, no instalas SDK, no rompes producción —monitoreamos desde afuera, sobre los mismos motores donde te buscan tus prospectos."
    >
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          borderTop: '1px solid #1f2422',
        }}
      >
        {STEPS.map((step, i) => (
          <Box
            key={step.num}
            sx={{
              py: { xs: 3, md: 4 },
              pr: { sm: i % 2 === 0 ? '20px' : 0, md: i < 3 ? '28px' : 0 },
              pl: { sm: i % 2 !== 0 ? '20px' : 0, md: i > 0 ? '28px' : 0 },
              borderRight: {
                xs: 'none',
                sm: i % 2 === 0 ? '1px solid #1f2422' : 'none',
                md: i < 3 ? '1px solid #1f2422' : 'none',
              },
              borderBottom: {
                xs: i < 3 ? '1px solid #1f2422' : 'none',
                sm: i < 2 ? '1px solid #1f2422' : 'none',
                md: 'none',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'primary.lightAccent',
                fontWeight: 600,
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                '&::after': {
                  content: '""',
                  flex: '1 1 0%',
                  maxWidth: '60px',
                  height: '1px',
                  background: 'linear-gradient(to right, #7ed7c5, transparent)',
                },
              }}
            >
              <T>{step.num}</T>
            </Box>
            <Typography
              variant="h4"
              component="h3"
              sx={{ color: 'common.white', mb: 1.5 }}
            >
              <T>{step.title}</T>
            </Typography>
            <Typography
              sx={{ fontSize: '14px', color: 'text.muted', lineHeight: 1.55 }}
            >
              <T>{step.desc}</T>
            </Typography>
          </Box>
        ))}
      </Box>
    </PageSection>
  );
}
